import {
  getComponentTab,
  getSizesOptions,
  updateDerivedFields,
  getFamilyTableOptions,
} from "./utils";

/*
 * Builds the expanded per-size combination rows from the selected specs.
 *
 * This logic used to live inside PartLibTable, which meant it only ran while
 * the Spec Library tab was mounted. Everything it needs — the selected spec
 * rows and the DB tables — is context state, so it lives here instead and the
 * context runs it whenever the spec selection changes. Estimation therefore has
 * its dropdown options without the Spec Library tab ever being opened.
 */

export const ALL_COMBINATION_TABS = [
  "Angles",
  "Beams",
  "Channels",
  "Tubes",
  "Flatbars",
  "Gratings",
  "Plates",
  "Bolts",
  "Nuts",
  "Washers",
];

/*
 * Annotate each selected spec row with the Part Lib tab it belongs to.
 */
function withTabNames(specRows, allTables) {
  return (specRows || []).map((row) => ({
    ...row,
    __tabName: getComponentTab(row?.Components, row?.Spec, allTables),
  }));
}

/*
 * spec -> tab -> component -> base rows
 */
function indexBySpecTabComponent(specRowsWithTab) {
  const index = new Map();

  for (const row of specRowsWithTab) {
    const spec = row?.Spec;
    const comp = row?.Components;
    const tab = row?.__tabName;
    if (!spec || !comp || !tab) continue;

    if (!index.has(spec)) index.set(spec, new Map());
    const tabMap = index.get(spec);
    if (!tabMap.has(tab)) tabMap.set(tab, new Map());
    const compMap = tabMap.get(tab);
    if (!compMap.has(comp)) compMap.set(comp, []);
    compMap.get(comp).push(row);
  }

  return index;
}

/*
 * Size options per `spec|component`, resolved once. getSizesOptions expands a
 * spec's size *range* (e.g. "1/2--4") into the discrete sizes listed in
 * structural_ranges_<Spec>, which is what the dropdowns need to offer.
 */
function buildSizeOptionsCache(specs, specRowsWithTab, allTables) {
  const cache = new Map();

  for (const spec of specs) {
    const rowsForSpec = specRowsWithTab.filter((r) => r?.Spec === spec);
    const comps = [
      ...new Set(rowsForSpec.map((r) => r?.Components).filter(Boolean)),
    ];

    for (const comp of comps) {
      const key = `${spec}|${comp}`;
      try {
        cache.set(
          key,
          getSizesOptions(specRowsWithTab, spec, comp, allTables) || [],
        );
      } catch (e) {
        console.error("getSizesOptions failed:", e);
        cache.set(key, []);
      }
    }
  }

  return cache;
}

/*
 * Auto-select a Family Table when exactly one option exists, mirroring
 * FamilyTableCell, so rows from tabs the user never visited still get one.
 */
function applyFamilyTable(row, allTables) {
  try {
    const familyOptions = getFamilyTableOptions(
      allTables,
      row.Components,
      row.Spec,
    );

    const uniqueNames = Array.from(
      new Set(
        (familyOptions || [])
          .map((cur) => String(cur?.Family_Name ?? "").trim())
          .filter((n) => n && n !== "-"),
      ),
    );

    if (uniqueNames.length === 1 && !row["Family Table"]) {
      const onlyName = uniqueNames[0];
      row["Family Table"] = onlyName;
      const tableTarget =
        familyOptions.find((cur) => cur.Family_Name === onlyName)
          ?.Family_Table || "";
      if (tableTarget) row.FamilyTableTarget = tableTarget;
    }
  } catch {}
}

/*
 * Expand the selected spec rows into one row per discrete size.
 */
export function buildCombinationRows(selectedSpecRows, allTables) {
  const specRowsWithTab = withTabNames(selectedSpecRows, allTables);
  if (!specRowsWithTab.length) return [];

  const specs = [
    ...new Set(specRowsWithTab.map((item) => item?.Spec).filter(Boolean)),
  ];
  if (!specs.length) return [];

  const specTabCompIndex = indexBySpecTabComponent(specRowsWithTab);
  const sizeOptionsCache = buildSizeOptionsCache(
    specs,
    specRowsWithTab,
    allTables,
  );

  const allRows = [];

  for (const tabName of ALL_COMBINATION_TABS) {
    for (const spec of specs) {
      const compMap = specTabCompIndex.get(spec)?.get(tabName);
      if (!compMap) continue;

      for (const [comp, baseRows] of compMap.entries()) {
        const sizes = sizeOptionsCache.get(`${spec}|${comp}`) || [];
        if (!baseRows?.length || !sizes.length) continue;

        for (const baseRow of baseRows) {
          const orgSizeRange = baseRow?.Size;

          const seen = new Set();
          for (const sizeObj of sizes.filter(
            (s) => s?.range === orgSizeRange,
          )) {
            const val = sizeObj?.value;
            if (!val || seen.has(val)) continue;
            seen.add(val);

            const newRow = {
              ...baseRow,
              select: false,
              TabName: tabName,
              Size: val,
              OriginalSizeRange: orgSizeRange,
              CategoryType: "Bulk",
            };

            try {
              updateDerivedFields(
                specRowsWithTab,
                (_rowIndex, key, value) => {
                  newRow[key] = value;
                },
                allTables,
                0,
                newRow,
              );
            } catch {}

            applyFamilyTable(newRow, allTables);

            delete newRow.__tabName;
            allRows.push(newRow);
          }
        }
      }
    }
  }

  return allRows;
}

/*
 * Carry user state forward across a rebuild: a checkbox ticked in the Spec
 * Library, or a row previously saved into partLibTable, must survive the specs
 * list changing.
 */
export function mergeWithPrevious(newRows, prevAllCombo, partLibTable) {
  return (newRows || []).map((newRow) => {
    const prevMatch = (prevAllCombo || []).find(
      (p) =>
        p?.Spec === newRow?.Spec &&
        p?.Components === newRow?.Components &&
        p?.Size === newRow?.Size &&
        p?.Material === newRow?.Material &&
        p?.TabName === newRow?.TabName,
    );

    if (prevMatch?.select) {
      return {
        ...newRow,
        ...prevMatch,
        select: true,
        "Family Table": prevMatch["Family Table"] ?? newRow["Family Table"],
        FamilyTableTarget:
          prevMatch.FamilyTableTarget ?? newRow.FamilyTableTarget,
      };
    }

    const savedTab = partLibTable?.[newRow?.TabName];
    if (savedTab?.length) {
      const savedRow = savedTab.find(
        (r) =>
          r?.Spec === newRow?.Spec &&
          r?.Components === newRow?.Components &&
          r?.Size === newRow?.Size &&
          r?.Material === newRow?.Material,
      );
      if (savedRow) {
        return {
          ...newRow,
          ...savedRow,
          select: true,
          TabName: newRow?.TabName,
          "Family Table": savedRow["Family Table"] || newRow["Family Table"],
          FamilyTableTarget:
            savedRow.FamilyTableTarget || newRow.FamilyTableTarget,
        };
      }
    }

    return newRow;
  });
}
